import React, { createContext, useContext, useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

const ThemeContext = createContext(null);
export const useTheme = () => useContext(ThemeContext);

// Available app themes:
//  - aurora   : default dark, cyan accents (public/browser default)
//  - ember    : dark, warm neon accents (ember/magenta/gold)
//  - light    : "Aurora White" soft light
//  - daylight : new colourful light (coral / violet / teal on a warm pearl)
//  - mpower   : dark, BMW "M" stripes (electric blue / navy / red) on black
//  - mlight   : light, electric-blue "M" accents on a cool pearl
export const THEMES = ["aurora", "ember", "light", "daylight", "mpower", "mlight"];
export const LIGHT_THEMES = ["light", "daylight", "mlight"];

const LEGACY = { dark: "aurora", light: "light" };

const isStandalone = () =>
  (typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true)) ||
  false;

const initialTheme = () => {
  const saved = localStorage.getItem("imperium-theme");
  if (saved) return LEGACY[saved] || saved;
  return isStandalone() ? "ember" : "aurora"; // installed app defaults to Ember
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(initialTheme);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "theme-ember", "theme-daylight", "theme-mpower", "theme-mlight");
    if (theme === "ember") root.classList.add("theme-ember");
    else if (theme === "light") root.classList.add("light");
    else if (theme === "daylight") root.classList.add("light", "theme-daylight");
    else if (theme === "mpower") root.classList.add("theme-mpower");
    else if (theme === "mlight") root.classList.add("light", "theme-mlight");
    localStorage.setItem("imperium-theme", theme);
  }, [theme]);

  // Quick toggle used by the header button: flip between the dark/light families.
  const toggle = () => chooseTheme(LIGHT_THEMES.includes(theme) ? "aurora" : "light");
  const isLight = LIGHT_THEMES.includes(theme);

  // User picked a theme → persist locally + on their account (follows across devices).
  const chooseTheme = (t) => {
    setTheme(t);
    api.put("/app-theme", { theme: t }).catch(() => {});
  };

  // Apply a theme coming from the server without re-persisting (hydration on login).
  const applyServerTheme = useCallback((t) => { if (THEMES.includes(t)) setTheme(t); }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme: chooseTheme, chooseTheme, applyServerTheme, toggle, isLight, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
};
